import './playerInfoModify.scss';
import React, {useCallback, useContext, useState} from 'react';
import {
    Card,
    Button,
    Space,
    Divider,
    Typography,
    Input,
    InputNumber,
    DatePicker,
    Row,
    Col, Tag, Modal
} from 'antd';
import {
    SettingOutlined,
    SaveOutlined,
    ClockCircleOutlined,
    ThunderboltOutlined,
    VideoCameraOutlined,
    WarningOutlined, ExclamationCircleOutlined
} from '@ant-design/icons';
import {internalApi} from '../../common/axios';
import {defaultPlayerData} from '../../shared/contexts/playerContext';
import {ENV, PASS_GRADE, PASS_TYPE, USER_STATE} from "../../shared/utils/enum";
import toast from "react-hot-toast";
import dayjs from "dayjs";
import {buildUrl} from "../../shared/utils/net";
import {AuthContext} from "../../shared/contexts/authContext";
import {DarkModeContext} from "../../shared/contexts/darkModeContext";
import IsLoading from "../../shared/components/IsLoading";
import ErrorPage from "../../shared/components/ErrorPage";
import {useStoreOnIndexList} from "../storeManager/useStoreManagerData";
import {getCurrentPassStatus} from "../../shared/utils/typeChecks";

const {Text, Title} = Typography;

const MONTHLY_TYPE = {
    ENERGY: 1,
    ENERGY_EXTEND: 2,
    AD: 3,
    AD_EXTEND: 4,
}

const PlayerInfoModify = ({playerData, setPlayerData, isChange, setIsChange, setDefaultState, onRefresh}) => {
    const {currentUser} = useContext(AuthContext);
    const {darkMode} = useContext(DarkModeContext);
    const [open, setOpen] = useState(false);
    const [extendDays, setExtendDays] = useState(1);
    const [loginDate, setLoginDate] = useState(null);
    const [paymentDate, setPaymentDate] = useState(null);
    const [paymentAmount, setPaymentAmount] = useState(0.99);
    const [newLevel, setNewLevel] = useState(null);
    const {data, isLoading, error} = useStoreOnIndexList();

    const WITHDRAW_REASON = {
        NONE: 0,
        SELF: 1,
        ADMIN: 2,
    }
    const getStateString = useCallback(() => {
        switch (playerData.userState) {
            case USER_STATE.WITHDRAW:
                return '탈퇴';
            case USER_STATE.SUSPEND:
                return '정지';
            default:
                return '정상';
        }
    }, [playerData.userState]);


    if (isLoading) return <IsLoading/>;
    if (error) return <ErrorPage error={error}/>;

    const handleSetPlayerState = (name, value) => {
        setPlayerData(prevState => ({
            ...prevState,
            [name]: value
        }));
    }

    const handleUserState = () => {
        if (isWithdraw) {
            toast.error('이미 탈퇴된 계정입니다.');
            return;
        }

        if (playerData.userState === USER_STATE.WITHDRAW) {
            handleSetPlayerState('stateReason', WITHDRAW_REASON.ADMIN);
        }

        const url = buildUrl('player', {
            userKey: playerData.userKey,
            userState: playerData.userState,
            stateReason: playerData.userState === USER_STATE.WITHDRAW
                ? WITHDRAW_REASON.ADMIN : playerData.userState === USER_STATE.DEFAULT
                    ? WITHDRAW_REASON.NONE : playerData.stateReason,
            stateDate: playerData.stateDate,
            pid: playerData.pid,
            ...(playerData.isDelete && {isDelete: true})
        });

        internalApi.delete(url).then((res) => {
            setDefaultState(playerData.userState);
            setIsChange(false);
            if (playerData.userState === USER_STATE.WITHDRAW) {
                setPlayerData(defaultPlayerData);
            } else {
                onRefresh?.();
            }
            toast.success(res.data.message);
        }).catch((err) => {
            toast.error(err.response.data.message);
        });
    };

    const handleOpenDialog = () => {
        if (isWithdraw) {
            const stateReason = playerData.stateReason === WITHDRAW_REASON.SELF ? '삭제' : '탈퇴';
            toast.error(`이미 ${stateReason}된 계정입니다.`);
            return;
        }

        if (playerData.userState === USER_STATE.SUSPEND) {
            if (playerData.stateDate < dayjs().unix()) {
                toast.error('정지 기간이 지났습니다. (수정 필요)');
                return;
            }
        }

        setOpen(true);
    };

    const handleCloseDialog = () => {
        setOpen(false);
    };

    const handleConfirmUserStateChange = () => {
        handleUserState();
        handleCloseDialog();
    };

    const isWithdraw = playerData.userState === USER_STATE.WITHDRAW && playerData.stateReason > WITHDRAW_REASON.NONE;


    const setChangeLastLogin = () => {
        const days = 1;
        const url = buildUrl('player/lastLogin', {
            userKey: playerData.userKey,
            days
        });

        internalApi.put(url).then((res) => {
            toast.success(res.data.message);
            onRefresh?.();
        }).catch((err) => {
            toast.error(err.response.data.message);
        });
    }

    const handleSetPass = async (passType, passGrade) => {
        if (!playerData.userKey) {
            toast.error("유저 키가 없습니다.");
            return;
        }
        let passIndex = 0;
        switch (passType) {
            case PASS_TYPE.MONTHLY:
                passIndex = playerData.playerInfo?.shopData?.ActivePass?.index || 0;
                break;
            case PASS_TYPE.WEEKLY:
                passIndex = playerData.playerInfo?.shopData?.ActivePass_Weekly?.[0]?.index || 0;
                break;
            case PASS_TYPE.SPECIAL:
                passIndex = playerData.playerInfo?.shopData?.ActivePass_Special?.index || 0;
                break;
            default:
                toast.error("잘못된 패스 타입입니다.");
                return;
        }

        try {
            await internalApi.post('/player/pass', {
                userKey: playerData.userKey,
                passType,
                passIndex,
                passGrade
            });
            toast.success("시즌 패스가 적용되었습니다.");
            onRefresh?.();
        } catch (e) {
            console.error("시즌 패스 적용 실패:", e);
            toast.error("시즌 패스 적용에 실패했습니다.");
        }
    }

    const handleRestoreEnergy = async () => {
        const energyInfo = getCurrentStoreInfo(playerData.playerInfo?.shopData?.DicPackageDataEnergyMission.NeedUserLevel);
        if (energyInfo.purchasesCount !== 0) {
            toast.error('이미 에너지 패키지를 구매한 상태입니다.');
            return;
        }
        try {
            await internalApi.post('/player/energy', {
                userKey: playerData.userKey,
                energyMissionIndex: energyInfo.Index
            });
            onRefresh?.();
        } catch (e) {
            console.error("에너지 패키지 적용 실패", e);
            toast.error("에너지 패키지 적용에 실패했습니다.");
        }
    }

    const handleRestoreChain = async () => {
        try {
            await internalApi.post('/player/chain', {
                userKey: playerData.userKey,
                chainPackageIndex: getCurrentStoreInfo(playerData.playerInfo?.shopData?.DicPackageDataChain.NeedUserLevel).Index
            });
            onRefresh?.();
        } catch (e) {
            console.error("체인 패키지 적용 실패", e);
            toast.error("체인 패키지 적용에 실패했습니다.");
        }
    }

    const handleSetMergeEventPass = async () => {
        if (!playerData.userKey) {
            toast.error("유저 키가 없습니다.");
            return;
        }
        try {
            await internalApi.post('/player/merge-event-pass', {
                userKey: playerData.userKey,
            });
            toast.success("머지 이벤트 패스가 적용되었습니다.");
            onRefresh?.();
        } catch (e) {
            console.error("머지 이벤트 패스 적용 실패:", e);
            toast.error("머지 이벤트 패스 적용에 실패했습니다.");
        }
    }

    const handleSetLuckyArcadeTicket = async () => {
        if (!playerData.userKey) {
            toast.error("유저 키가 없습니다.");
            return;
        }
        try {
            await internalApi.post('/player/lucky-arcade/ticket', {
                userKey: playerData.userKey,
            });
            toast.success("럭키 아케이드 티켓이 지급되었습니다.");
            onRefresh?.();
        } catch (e) {
            console.error("럭키 아케이드 티켓 지급 실패:", e);
            toast.error(e?.response?.data?.message || "럭키 아케이드 티켓 지급에 실패했습니다.");
        }
    }

    const handleSetShoppingRoadPass = async () => {
        if (!playerData.userKey) {
            toast.error("유저 키가 없습니다.");
            return;
        }
        try {
            await internalApi.post('/player/shoppingRoad/pass', {
                userKey: playerData.userKey,
            });
            toast.success("쇼핑로드 이벤트 패스가 적용되었습니다.");
            onRefresh?.();
        } catch (e) {
            console.error("쇼핑로드 이벤트 패스 적용 실패:", e);
            toast.error("쇼핑로드 이벤트 패스 적용에 실패했습니다.");
        }
    }

    const handleMonthlyPassRestore = async () => {
        if (!playerData.userKey) {
            toast.error("유저 키가 없습니다.");
            return;
        }
        try {
            await internalApi.post('/player/passRestore', {
                userKey: playerData.userKey,
            });
            toast.success("시즌 패스가 복구 되었습니다.");
            onRefresh?.();
        } catch (e) {
            console.error("시즌 패스 적용 실패:", e);
            toast.error("시즌 패스 복구가 실패했습니다.");
        }
    }

    const handleSetMonthly = async (monthlyType) => {
        try {
            await internalApi.post('/player/playInfo/monthly', {
                userKey: playerData.userKey,
                monthlyType
            });
            toast.success("플레이 정보가 저장되었습니다.");
            onRefresh?.();
        } catch (e) {
            toast.error("플레이 정보 저장에 실패했습니다.");
        }
    }

    const handleExtendMonthly = async (monthlyTypeToExtend) => {
        if (!playerData.userKey) {
            toast.error("유저 키가 없습니다.");
            return;
        }
        if (extendDays <= 0) {
            toast.error("연장할 일수를 1일 이상으로 입력해주세요.");
            return;
        }

        try {
            await internalApi.post('/player/playInfo/monthly/extend', {
                userKey: playerData.userKey,
                monthlyType: monthlyTypeToExtend,
                extendDays: extendDays
            });
            toast.success("월정액이 성공적으로 연장되었습니다.");
            onRefresh?.();
        } catch (e) {
            console.error("월정액 연장 실패:", e);
            toast.error("월정액 연장에 실패했습니다.");
        }
    }

    const getCurrentStoreInfo = (storeList) => {
        if (!storeList || storeList.length === 0 || data.length === 0) {
            return null;
        }
        for (const store of Object.values(storeList)) {
            if (data.includes(store.Index)) {
                return store;
            }
        }
        return null;
    }

    const handleAddLoginLog = async () => {
        if (!playerData.userKey) return toast.error('유저 키가 없습니다.');
        if (!loginDate) return toast.error('로그인 날짜를 선택해주세요.');
        try {
            await internalApi.post('/player/log/login', {
                userKey: playerData.userKey,
                pid: playerData.pid,
                name: playerData.name,
                loginDate: loginDate.unix(),
            });
            toast.success('로그인 기록이 추가되었습니다.');
            setLoginDate(null);
        } catch (e) {
            toast.error(e?.response?.data?.message || '로그인 기록 추가 실패');
        }
    };

    const handleAddPaymentLog = async () => {
        if (!playerData.userKey) return toast.error('유저 키가 없습니다.');
        if (!paymentDate) return toast.error('결제 날짜를 선택해주세요.');
        if (!paymentAmount || paymentAmount <= 0) return toast.error('결제 금액을 입력해주세요.');
        try {
            await internalApi.post('/player/log/payment', {
                userKey: playerData.userKey,
                pid: playerData.pid,
                name: playerData.name,
                price: paymentAmount,
                paymentDate: paymentDate.format('YYYY-MM-DD HH:mm:ss'),
            });
            toast.success('결제 기록이 추가되었습니다.');
            setPaymentDate(null);
            setPaymentAmount(0.99);
        } catch (e) {
            toast.error(e?.response?.data?.message || '결제 기록 추가 실패');
        }
    };

    const handleSetUserLevel = async () => {
        if (!playerData.userKey) return toast.error('유저 키가 없습니다.');
        if (!newLevel || newLevel < 1) return toast.error('변경할 레벨을 입력해주세요.');
        try {
            await internalApi.put('/player/level', {
                userKey: playerData.userKey,
                lv: newLevel,
            });
            toast.success('레벨이 변경되었습니다.');
            setNewLevel(null);
            onRefresh?.();
        } catch (e) {
            toast.error(e?.response?.data?.message || '레벨 변경 실패');
        }
    };

    const handleResetPersonalPackage = () => {
        if (!playerData.userKey) return toast.error('유저 키가 없습니다.');
        Modal.confirm({
            title: '개인화 패키지 초기화',
            icon: <ExclamationCircleOutlined style={{color: '#ff4d4f'}}/>,
            content: `정말로 ${playerData.playerInfo?.userInfo?.Nickname || '사용자'} 님의 개인화 패키지를 초기화하시겠습니까?`,
            okText: '초기화',
            okButtonProps: {danger: true},
            cancelText: '취소',
            onOk: async () => {
                try {
                    const url = buildUrl('player/personalPackage', {userKey: playerData.userKey});
                    await internalApi.delete(url);
                    toast.success('개인화 패키지가 초기화되었습니다.');
                } catch (e) {
                    toast.error(e?.response?.data?.message || '개인화 패키지 초기화 실패');
                }
            },
        });
    };

    const handleResetSegmentCache = () => {
        if (!playerData.userKey) return toast.error('유저 키가 없습니다.');
        Modal.confirm({
            title: '세그먼트 캐시 삭제',
            icon: <ExclamationCircleOutlined style={{color: '#ff4d4f'}}/>,
            content: `${playerData.playerInfo?.userInfo?.Nickname || '사용자'} 님의 세그먼트 캐시(결제/로그인 통계, 세그먼트 이벤트 배정, 레벨별 미해당 기록)를 삭제합니다. 다음 접속 시 다시 판정되며, 배정된 세그먼트 이벤트가 바뀔 수 있습니다.`,
            okText: '삭제',
            okButtonProps: {danger: true},
            cancelText: '취소',
            onOk: async () => {
                try {
                    await internalApi.delete(buildUrl('player/segmentCache', {userKey: playerData.userKey}));
                    toast.success('세그먼트 캐시가 삭제되었습니다.');
                } catch (e) {
                    toast.error(e?.response?.data?.message || '세그먼트 캐시 삭제 실패');
                }
            },
        });
    };

    const cardTitle = (
        <Space align="center" size="middle">
            <div className="title-icon">
                <SettingOutlined/>
            </div>
            <div>
                <Title level={4} style={{margin: 0}}>
                    Player Management
                </Title>
                <Text type="secondary" style={{fontSize: '12px'}}>
                    플레이어 관리 도구
                </Text>
            </div>
        </Space>
    );

    return (
        <Card
            title={cardTitle}
            className={`player-modify-card ${darkMode ? 'dark-theme' : 'light-theme'}`}
            styles={{
                header: {
                    borderBottom: `1px solid var(--border-color)`,
                    background: `var(--header-background)`,
                    borderTopLeftRadius: '8px',
                    borderTopRightRadius: '8px'
                },
                body: {
                    padding: '24px',
                    background: `var(--card-background)`
                }
            }}
            style={{
                borderRadius: '8px',
                border: `1px solid var(--border-color)`,
                background: `var(--card-background)`,
                boxShadow: 'var(--card-shadow)'
            }}
        >
            {/* 계정 상태 관리 섹션 */}
            <div className="modify-section">
                <div className="section-header">
                    <WarningOutlined className="section-icon"/>
                    <Text strong className="section-title">계정 상태 관리</Text>
                </div>
                <Space direction="vertical" style={{width: '100%'}} size="middle">
                    <Row gutter={[12, 12]}>
                        <Col xs={24} sm={12}>
                            <Button
                                type="primary"
                                icon={<SaveOutlined/>}
                                onClick={handleOpenDialog}
                                disabled={!playerData.userKey || !isChange}
                                className="action-button primary-button"
                                block
                            >
                                상태 변경
                            </Button>
                        </Col>
                        {currentUser.target !== ENV.PRODUCTION && (
                            <Col xs={24} sm={12}>
                                <Button
                                    icon={<ClockCircleOutlined/>}
                                    onClick={setChangeLastLogin}
                                    disabled={!playerData.userKey}
                                    className="action-button secondary-button"
                                    block
                                >
                                    로그인 날짜 변경
                                </Button>
                            </Col>
                        )}
                    </Row>
                </Space>
            </div>

            <Divider className="section-divider"/>

            {/* 월정액 적용 섹션 */}
            <div className="modify-section">
                <div className="section-header">
                    <ThunderboltOutlined className="section-icon"/>
                    <Text strong className="section-title">월정액 적용</Text>
                </div>
                <Space direction="vertical" style={{width: '100%'}} size="middle">
                    <Row gutter={[12, 12]}>
                        <Col xs={24} sm={12}>
                            <Button
                                icon={<ThunderboltOutlined/>}
                                disabled={!playerData.userKey}
                                onClick={() => handleSetMonthly(MONTHLY_TYPE.ENERGY)}
                                className="action-button energy-button"
                                block
                            >
                                에너지 월정액 적용
                            </Button>
                        </Col>
                        <Col xs={24} sm={12}>
                            <Button
                                icon={<VideoCameraOutlined/>}
                                disabled={!playerData.userKey}
                                onClick={() => handleSetMonthly(MONTHLY_TYPE.AD)}
                                className="action-button energy-button"
                                block
                            >
                                광고 월정액 적용
                            </Button>
                        </Col>
                    </Row>
                </Space>
            </div>

            <Divider className="section-divider"/>

            {/* 월정액 연장 섹션 */}
            <div className="modify-section">
                <div className="section-header">
                    <ClockCircleOutlined className="section-icon"/>
                    <Text strong className="section-title">월정액 연장</Text>
                </div>

                <Space direction="vertical" style={{width: '100%'}} size="middle">
                    <div className="extend-input-container">
                        <Text className="input-label">연장 일수:</Text>
                        <Input
                            type="number"
                            value={extendDays}
                            onChange={(e) => setExtendDays(Number(e.target.value))}
                            min={1}
                            suffix="일"
                            className="extend-input"
                            placeholder="연장할 일수 입력"
                        />
                    </div>

                    <Row gutter={[12, 12]}>
                        <Col xs={24} sm={12}>
                            <Button
                                icon={<ThunderboltOutlined/>}
                                disabled={!playerData.userKey || extendDays <= 0}
                                onClick={() => handleExtendMonthly(MONTHLY_TYPE.ENERGY_EXTEND)}
                                className="action-button energy-button"
                                block
                            >
                                에너지 월정액 연장
                            </Button>
                        </Col>
                        <Col xs={24} sm={12}>
                            <Button
                                icon={<VideoCameraOutlined/>}
                                disabled={!playerData.userKey || extendDays <= 0}
                                onClick={() => handleExtendMonthly(MONTHLY_TYPE.AD_EXTEND)}
                                className="action-button energy-button"
                                block
                            >
                                광고 월정액 연장
                            </Button>
                        </Col>
                    </Row>
                </Space>
            </div>

            <Divider className="section-divider"/>

            {/* 시즌패스 적용 섹션 */}
            <div className="modify-section">
                <div className="section-header">
                    <ThunderboltOutlined className="section-icon"/>
                    <Text strong className="section-title">시즌패스 적용</Text>
                </div>
                {/* 시즌패스 상태 표시 */}
                <div style={{marginBottom: 12}}>
                    <Text style={{ display: 'block' }}>
                        월간 패스: {(() => {
                        const status = getCurrentPassStatus(playerData.playerInfo?.shopData?.ActivePass);
                        return status ? <Tag color="blue">{status}</Tag> : <Tag color="default">구매 내역 없음</Tag>;
                    })()}
                        &nbsp;|&nbsp;
                        주간 패스: {(() => {
                        const status = getCurrentPassStatus(playerData.playerInfo?.shopData?.ActivePass_Weekly?.[0]);
                        return status ? <Tag color="gold">{status}</Tag> : <Tag color="default">구매 내역 없음</Tag>;
                    })()}
                        &nbsp;|&nbsp;
                        이벤트 패스: {(() => {
                        const eventPass =
                            playerData.playerInfo?.shopData?.ActiveEventPass?.[playerData.playerInfo?.mergeEventData?.ActiveMergeEventState?.id];

                        if (!playerData.playerInfo?.mergeEventData?.ActiveMergeEventState ||
                            eventPass === undefined) {
                            return <Tag color="default">이벤트 미참여</Tag>;
                        }
                        const status = eventPass?.purchasedProduct?.[0];
                        return status ? <Tag color="gold">{status}</Tag> : <Tag color="default">구매 내역 없음</Tag>;
                    })()}
                    </Text>
                    <Text style={{ display: 'block' }}>
                        스페셜 패스: {(() => {
                        const status = getCurrentPassStatus(playerData.playerInfo?.shopData?.ActivePass_Special);
                        return status ? <Tag color="blue">{status}</Tag> : <Tag color="default">구매 내역 없음</Tag>;
                    })()}
                        &nbsp;|&nbsp;
                        쇼핑로드 패스: {(() => {
                        if (playerData.playerInfo?.liveEventCache?.ShoppingRoadData) {
                            const currentShoppingRoad = Object.values(playerData.playerInfo?.liveEventCache?.ShoppingRoadData)[0] || {};
                            if (currentShoppingRoad === {}) {
                                return <Tag color="default">쇼핑 로드 내역 없음</Tag>;
                            }
                            const currentShoppingRoadId = currentShoppingRoad.seqId;

                            const status = getCurrentPassStatus(playerData.playerInfo?.shopData?.ActiveEventPass[currentShoppingRoadId]);
                            return status ? <Tag color="blue">{status}</Tag> : <Tag color="default">구매 내역 없음</Tag>;
                        } else {
                            return <Tag color="default">쇼핑 로드 내역 없음</Tag>;
                        }
                    })()}
                    </Text>
                </div>
                <Space direction="vertical" style={{width: '100%'}} size="middle">
                    <Row gutter={[12, 12]}>
                        {/* 월간패스 */}
                        <Col xs={12} sm={6}>
                            <Button
                                icon={<ThunderboltOutlined/>}
                                disabled={!playerData.userKey}
                                onClick={() => handleSetPass(PASS_TYPE.MONTHLY, PASS_GRADE.VIP)}
                                className="action-button energy-button"
                                block
                            >
                                월간패스 VIP
                            </Button>
                        </Col>
                        <Col xs={12} sm={6}>
                            <Button
                                icon={<ThunderboltOutlined/>}
                                disabled={!playerData.userKey}
                                onClick={() => handleSetPass(PASS_TYPE.MONTHLY, PASS_GRADE.UPGRADE_TO_LUXURY)}
                                className="action-button energy-button"
                                block
                            >
                                월간패스 VIP→럭셔리
                            </Button>
                        </Col>
                        <Col xs={12} sm={6}>
                            <Button
                                icon={<ThunderboltOutlined/>}
                                disabled={!playerData.userKey}
                                onClick={() => handleSetPass(PASS_TYPE.MONTHLY, PASS_GRADE.LUXURY)}
                                className="action-button energy-button"
                                block
                            >
                                월간패스 럭셔리
                            </Button>
                        </Col>
                        <Col xs={12} sm={6}>
                            <Button
                                icon={<ThunderboltOutlined/>}
                                disabled={!playerData.userKey}
                                onClick={handleMonthlyPassRestore}
                                className="action-button energy-button"
                                block
                            >
                                월간 패스 복구
                            </Button>
                        </Col>
                    </Row>
                    <Row gutter={[12, 12]}>
                        {/* 주간패스 */}
                        <Col xs={12} sm={6}>
                            <Button
                                icon={<ThunderboltOutlined/>}
                                disabled={!playerData.userKey}
                                onClick={() => handleSetPass(PASS_TYPE.WEEKLY, PASS_GRADE.LUXURY)}
                                className="action-button energy-button"
                                block
                            >
                                주간패스 럭셔리
                            </Button>
                        </Col>
                    </Row>
                    <Row gutter={[12, 12]}>
                        <Col xs={12} sm={6}>
                            <Button
                                icon={<ThunderboltOutlined/>}
                                disabled={!playerData.userKey}
                                onClick={handleSetMergeEventPass}
                                className="action-button energy-button"
                                block
                            >
                                머지 이벤트 패스
                            </Button>
                        </Col>
                        <Col xs={12} sm={6}>
                            <Button
                                icon={<ThunderboltOutlined/>}
                                disabled={!playerData.userKey}
                                onClick={handleSetShoppingRoadPass}
                                className="action-button energy-button"
                                block
                            >
                                쇼핑로드 이벤트 패스
                            </Button>
                        </Col>
                    </Row>
                    <Row gutter={[12, 12]}>
                        {/* 스페셜패스 */}
                        <Col xs={12} sm={6}>
                            <Button
                                icon={<ThunderboltOutlined/>}
                                disabled={!playerData.userKey}
                                onClick={() => handleSetPass(PASS_TYPE.SPECIAL, PASS_GRADE.VIP)}
                                className="action-button energy-button"
                                block
                            >
                                스페셜패스 VIP
                            </Button>
                        </Col>
                        <Col xs={12} sm={6}>
                            <Button
                                icon={<ThunderboltOutlined/>}
                                disabled={!playerData.userKey}
                                onClick={() => handleSetPass(PASS_TYPE.SPECIAL, PASS_GRADE.UPGRADE_TO_LUXURY)}
                                className="action-button energy-button"
                                block
                            >
                                스페셜패스 VIP→럭셔리
                            </Button>
                        </Col>
                        <Col xs={12} sm={6}>
                            <Button
                                icon={<ThunderboltOutlined/>}
                                disabled={!playerData.userKey}
                                onClick={() => handleSetPass(PASS_TYPE.SPECIAL, PASS_GRADE.LUXURY)}
                                className="action-button energy-button"
                                block
                            >
                                스페셜패스 럭셔리
                            </Button>
                        </Col>
                    </Row>
                </Space>
            </div>

            <Divider className="section-divider"/>

            {/*기타 패키지 적용*/}
            <div className="modify-section">
                <div className="section-header">
                    <ThunderboltOutlined className="section-icon"/>
                    <Text strong className="section-title">기타 패키지 적용</Text>
                </div>
                {/* 상태 표시 */}
                <div style={{marginBottom: 12}}>
                    <Text>
                        [에너지 패키지] {(() => {
                        const energyInfo = getCurrentStoreInfo(playerData.playerInfo?.shopData?.DicPackageDataEnergyMission?.NeedUserLevel);
                        const status = energyInfo?.purchasesCount || 0;
                        const body = status ? <Tag color="blue">구매함</Tag> : <Tag color="default">구매 내역 없음</Tag>;
                        return <>
                            현재 진행 인덱스: {energyInfo?.Index || '없음'} ({body})
                        </>
                    })()}
                        &nbsp;|&nbsp;
                        [체인 패키지 진행도] {(() => {
                        const chainInfo = getCurrentStoreInfo(playerData.playerInfo?.shopData?.DicPackageDataChain?.NeedUserLevel);
                        const status = chainInfo?.purchasesCount || 0;
                        const body = status ? <Tag color="blue">{status}단계</Tag> : <Tag color="default">진행 내역 없음</Tag>;
                        return <>
                            현재 진행 인덱스: {chainInfo?.Index || '없음'} ({body})
                        </>
                    })()}
                    </Text>
                </div>
                <Space direction="vertical" style={{width: '100%'}} size="middle">
                    <Row gutter={[12, 12]}>
                        {/* 월간패스 */}
                        <Col xs={24} sm={8}>
                            <Button
                                icon={<ThunderboltOutlined/>}
                                disabled={!playerData.userKey}
                                onClick={handleRestoreEnergy}
                                className="action-button energy-button"
                                block
                            >
                                에너지 패키지 적용
                            </Button>
                        </Col>
                        <Col xs={24} sm={8}>
                            <Button
                                icon={<ThunderboltOutlined/>}
                                disabled={!playerData.userKey}
                                onClick={handleRestoreChain}
                                className="action-button energy-button"
                                block
                            >
                                체인패키지 다음 단계 처리
                            </Button>
                        </Col>
                        <Col xs={24} sm={8}>
                            <Button
                                icon={<ThunderboltOutlined/>}
                                disabled={!playerData.userKey}
                                onClick={handleSetLuckyArcadeTicket}
                                className="action-button energy-button"
                                block
                            >
                                럭키 아케이드 티켓 지급
                            </Button>
                        </Col>

                    </Row>
                </Space>
            </div>

            {/* 로그기록 추가 섹션 (테스트용, 비-Live 환경만 노출) */}
            {currentUser.target !== ENV.PRODUCTION && (
                <>
                    <Divider className="section-divider"/>
                    <div className="modify-section">
                        <div className="section-header">
                            <ClockCircleOutlined className="section-icon"/>
                            <Text strong className="section-title">로그기록 추가</Text>
                        </div>
                        <Space direction="vertical" style={{width: '100%'}} size="middle">
                            {/* 로그인 기록 */}
                            <Row gutter={[12, 8]} align="middle">
                                <Col flex="none">
                                    <Text style={{whiteSpace: 'nowrap'}}>로그인 기록</Text>
                                </Col>
                                <Col flex="auto">
                                    <DatePicker
                                        value={loginDate}
                                        onChange={setLoginDate}
                                        style={{width: '100%'}}
                                        size="small"
                                        placeholder="로그인 날짜 선택"
                                        disabledDate={d => d && d.isAfter(dayjs())}
                                    />
                                </Col>
                                <Col flex="none">
                                    <Button
                                        size="small"
                                        disabled={!playerData.userKey || !loginDate}
                                        onClick={handleAddLoginLog}
                                    >
                                        추가
                                    </Button>
                                </Col>
                            </Row>
                            {/* 결제 기록 */}
                            <Row gutter={[12, 8]} align="middle">
                                <Col flex="none">
                                    <Text style={{whiteSpace: 'nowrap'}}>결제 기록</Text>
                                </Col>
                                <Col flex="auto">
                                    <DatePicker
                                        value={paymentDate}
                                        onChange={setPaymentDate}
                                        showTime={{format: 'HH:mm'}}
                                        format="YYYY-MM-DD HH:mm"
                                        style={{width: '100%'}}
                                        size="small"
                                        placeholder="결제 날짜/시간 선택"
                                        disabledDate={d => d && d.isAfter(dayjs())}
                                    />
                                </Col>
                                <Col flex="none">
                                    <InputNumber
                                        value={paymentAmount}
                                        onChange={setPaymentAmount}
                                        min={0.01}
                                        step={0.01}
                                        precision={2}
                                        size="small"
                                        addonBefore="$"
                                        style={{width: 110}}
                                        placeholder="금액(USD)"
                                    />
                                </Col>
                                <Col flex="none">
                                    <Button
                                        size="small"
                                        disabled={!playerData.userKey || !paymentDate || !paymentAmount}
                                        onClick={handleAddPaymentLog}
                                    >
                                        추가
                                    </Button>
                                </Col>
                            </Row>
                        </Space>
                    </div>
                </>
            )}

            {/* 실제레벨 변경 섹션 */}
            <Divider className="section-divider"/>
            <div className="modify-section">
                <div className="section-header">
                    <SettingOutlined className="section-icon"/>
                    <Text strong className="section-title">실제레벨 변경</Text>
                </div>
                <Space direction="vertical" style={{width: '100%'}} size="middle">
                    <Row gutter={[12, 8]} align="middle">
                        <Col flex="none">
                            <Text style={{whiteSpace: 'nowrap'}}>
                                현재 레벨: <Text strong>{playerData.lv ?? '-'}</Text>
                            </Text>
                        </Col>
                        <Col flex="auto">
                            <InputNumber
                                value={newLevel}
                                onChange={setNewLevel}
                                min={1}
                                placeholder="변경할 레벨 입력"
                                style={{width: '100%'}}
                            />
                        </Col>
                        <Col flex="none">
                            <Button
                                type="primary"
                                disabled={!playerData.userKey || !newLevel}
                                onClick={handleSetUserLevel}
                            >
                                변경
                            </Button>
                        </Col>
                    </Row>
                </Space>
            </div>

            {/* 개인화 패키지 초기화 섹션 */}
            <Divider className="section-divider"/>
            <div className="modify-section">
                <div className="section-header">
                    <WarningOutlined className="section-icon"/>
                    <Text strong className="section-title">개인화 패키지 초기화</Text>
                </div>
                <Space direction="vertical" style={{width: '100%'}} size="middle">
                    <Row gutter={[12, 8]}>
                        <Col xs={24} sm={12}>
                            <Button
                                danger
                                icon={<WarningOutlined/>}
                                disabled={!playerData.userKey}
                                onClick={handleResetPersonalPackage}
                                block
                            >
                                개인화 패키지 초기화
                            </Button>
                        </Col>
                        <Col xs={24} sm={12}>
                            <Button
                                danger
                                icon={<WarningOutlined/>}
                                disabled={!playerData.userKey}
                                onClick={handleResetSegmentCache}
                                block
                            >
                                세그먼트 캐시 삭제
                            </Button>
                        </Col>
                    </Row>
                </Space>
            </div>

            {/* 확인 모달 */}
            <Modal
                title={
                    <Space>
                        <ExclamationCircleOutlined style={{color: '#ff4d4f'}}/>
                        <span style={{color: '#ff4d4f'}}>(!주의!)</span>
                        계정 <Tag color="warning">{getStateString()}</Tag> 확인
                    </Space>
                }
                open={open}
                onCancel={handleCloseDialog}
                className={`confirm-modal ${darkMode ? 'dark-theme' : 'light-theme'}`}
                footer={[
                    <Button key="cancel" onClick={handleCloseDialog}>
                        취소
                    </Button>,
                    <Button
                        key="confirm"
                        type="primary"
                        danger={playerData.userState === USER_STATE.WITHDRAW}
                        onClick={handleConfirmUserStateChange}
                    >
                        확인
                    </Button>
                ]}
            >
                <Space direction="vertical" size="middle" style={{width: '100%'}}>
                    <Text>
                        정말로 <Text strong style={{color: '#1677ff'}}>
                        {playerData.playerInfo?.userInfo?.Nickname || '사용자'}
                    </Text> 님의 계정을 <Tag color="warning">{getStateString()}상태</Tag>로 설정 하시겠습니까?
                    </Text>

                    {playerData.userState === USER_STATE.WITHDRAW && (
                        <Text type="danger" strong>
                            <ExclamationCircleOutlined/> 이 작업은 되돌릴 수 없습니다.
                        </Text>
                    )}
                </Space>
            </Modal>
        </Card>
    );
};

export default PlayerInfoModify;

